export type FreshnessTone = 'gray' | 'green' | 'yellow' | 'red';

export interface FreshnessDisplay {
	label: string;
	tone: FreshnessTone;
	title: string;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Cards poll on a couple-of-minute cadence, so an age past these bounds means the poll stalled
 * (card scrolled out of view, tab backgrounded with alerts off, request failing) rather than slow data.
 */
const STALE_MS = 5 * MINUTE_MS;
const VERY_STALE_MS = 15 * MINUTE_MS;

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'} ago`;

/** Badge label/tone for how long ago a card's calls were fetched. `null` age means never fetched. */
export function formatFreshness(ageMs: number | null): FreshnessDisplay {
	if (ageMs == null || !Number.isFinite(ageMs)) {
		return { label: '—', tone: 'gray', title: 'Not loaded yet' };
	}

	const age = Math.max(0, ageMs);
	const tone: FreshnessTone = age < STALE_MS ? 'green' : age < VERY_STALE_MS ? 'yellow' : 'red';

	const minutes = Math.floor(age / MINUTE_MS);
	if (minutes < 1) {
		return { label: 'now', tone, title: 'Updated just now' };
	}
	if (minutes < 60) {
		return { label: `${minutes}m`, tone, title: `Updated ${plural(minutes, 'minute')}` };
	}

	const hours = Math.floor(age / HOUR_MS);
	return { label: `${hours}h`, tone, title: `Updated ${plural(hours, 'hour')}` };
}
